import { useCallback } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { usePercentageSlider } from "Hooks";
import { ReallocateQuotasForm } from "../../types";

type UseQuotaSplit = {
  portalPercentageFieldName: keyof ReallocateQuotasForm;
  cliPercentageFieldName: keyof ReallocateQuotasForm;
  portalCapacityBytes: number;
  cliTotalAllocationBytes: number;
  totalCapacityBytes: number;
};

const useQuotaSplit = ({
  portalPercentageFieldName,
  cliPercentageFieldName,
  portalCapacityBytes,
  cliTotalAllocationBytes,
  totalCapacityBytes,
}: UseQuotaSplit) => {
  const minCliPercentage =
    totalCapacityBytes > 0
      ? Math.ceil((cliTotalAllocationBytes / totalCapacityBytes) * 100)
      : 0;
  const currentPortalPercentage =
    totalCapacityBytes > 0
      ? Math.round((portalCapacityBytes / totalCapacityBytes) * 100)
      : 50;
  const {
    schema,
    formDefaultValues,
    getSliderPercentages,
    getRemainingPercentageValueFromInput,
  } = usePercentageSlider<ReallocateQuotasForm>({
    firstPercentageName: portalPercentageFieldName,
    secondPercentageName: cliPercentageFieldName,
    defaultSliderValue:
      100 - minCliPercentage < currentPortalPercentage
        ? 100 - minCliPercentage
        : currentPortalPercentage,
    firstPercentageMax: 100 - minCliPercentage,
    secondPercentageMin: minCliPercentage,
  });

  const formMethods = useForm<ReallocateQuotasForm>({
    resolver: yupResolver(schema),
    defaultValues: formDefaultValues,
    criteriaMode: "all",
  });

  const { watch, setValue } = formMethods;

  const portal_percentage = watch(portalPercentageFieldName);
  const { firstPercentage: portalPercentage, secondPercentage: cliPercentage } =
    getSliderPercentages(portal_percentage);

  const onCliPercentageChange = useCallback(
    (cliPercentageInputValue: string): void => {
      setValue(
        portalPercentageFieldName,
        getRemainingPercentageValueFromInput(cliPercentageInputValue),
      );
    },
    [setValue, portalPercentageFieldName, getRemainingPercentageValueFromInput],
  );

  const onPercentageSliderChange = (value: number) => {
    const {
      firstPercentage: portalPercentage,
      secondPercentage: cliPercentage,
    } = getSliderPercentages(value);

    setValue(portalPercentageFieldName, portalPercentage);
    setValue(cliPercentageFieldName, cliPercentage);
  };

  return {
    formMethods,
    portalPercentage,
    cliPercentage,
    onCliPercentageChange,
    onPercentageSliderChange,
  };
};

export default useQuotaSplit;
